/** Column orders of the tables: a list of keys (a field, ascending or descending), the first the main one. In a link
 *  or an API parameter a key is ``field`` or ``-field`` (descending). */

export type SortKey<F extends string = string> = { field: F; descending: boolean };

/** How a column is ordered: its direction and place (1-based). */
export type SortPlace = { descending: boolean; place: number };

/** ``field`` / ``-field`` as a key and back. */
export const sortKeyOf = (text: string): SortKey => ({ field: text.replace(/^-/, ""), descending: text.startsWith("-") });
export const sortText = (k: SortKey) => (k.descending ? `-${k.field}` : k.field);

/** A click on a column's header: alone it becomes the only key (ascending, then descending, then off); with ``add``
 *  (Shift) it is added after the others (and turns descending, then off, where it is a key already). */
export function nextSort<F extends string>(keys: SortKey<F>[], field: F, add = false): SortKey<F>[] {
  const at = keys.findIndex((k) => k.field === field);
  const current = at >= 0 ? keys[at] : undefined;
  const turned = { field, descending: !current?.descending };
  if (!add) {
    if (keys.length !== 1 || !current) return [{ field, descending: false }];
    return current.descending ? [] : [turned];
  }
  if (!current) return [...keys, { field, descending: false }];
  if (current.descending) return keys.filter((_, i) => i !== at);
  return keys.map((k, i) => (i === at ? turned : k));
}

/** How a column is ordered, none when it is not a key. */
export function sortedBy(keys: SortKey[], field: string): SortPlace | null {
  const at = keys.findIndex((k) => k.field === field);
  return at < 0 ? null : { descending: !!keys[at]?.descending, place: at + 1 };
}
